/**
 * CommandPalette — globale Schnellsuche/Befehlspalette (⌘K / Strg-K).
 *
 * Portiert aus netzfabriks-erp (dependency-frei: kein cmdk/kbar/fuse, kein
 * Radix-Dialog). Scoring/Ranking, Overlay, Keyboard-Handling und der
 * 180-ms-Debounce entsprechen 1:1 dem Vorbild.
 *
 * Zwei-Schichten-Modell (Geschwindigkeit):
 *   - Seiten + Aktionen + Dokumentation: rein client-seitig, synchron pro
 *     Tastendruck gescort → instant, 0 Netzwerk.
 *   - Mitarbeiter: 180 ms debounced an api.employees.list({ q, size }),
 *     via React-Query (staleTime 30 s, automatischer Request-Cancel).
 *
 * Tastatur:
 *   ⌘/Strg+K  öffnen/schließen (Toggle, im MainLayout registriert)
 *   ↑ / ↓     navigieren
 *   Enter     auswählen
 *   Escape    schließen
 */

import { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { Loader2, Search, Users } from "lucide-react"
import { api } from "@/services/api"
import { hasPermission } from "@/lib/permissions"
import { useAuthStore } from "@/store/auth"
import { cn } from "@/lib/utils"
import { useLinks } from "@/components/AppSwitcher"
import {
  actionCommands,
  buildAppCommands,
  buildDocCommands,
  pageCommands,
  type Command,
  type CommandGroup,
} from "./commandSources"

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Maximale Zahl an Doku-Treffern, damit lange Kapitel die Liste nicht fluten */
const MAX_DOC_RESULTS = 5
/** Server-Limit für die Mitarbeiter-Suche */
const EMPLOYEE_SEARCH_SIZE = 8

// ---------------------------------------------------------------------------
// Scoring/Ranking — 1:1 aus netzfabrik übernommen.
// ---------------------------------------------------------------------------

function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
}

function acronym(value: string): string {
  return normalize(value)
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
}

function fuzzyMatch(needle: string, haystack: string): boolean {
  if (!needle) return true
  const n = needle.toLowerCase()
  const h = haystack.toLowerCase()
  if (h.includes(n)) return true
  // Akzeptiere auch nicht-zusammenhaengende Treffer (z.B. "ujg" -> "Urlaub genehmigen")
  let i = 0
  for (const c of h) {
    if (c === n[i]) i++
    if (i === n.length) return true
  }
  return false
}

function scoreItem(query: string, item: Command): number | null {
  const q = normalize(query.trim())
  if (!q) return 0
  const title = normalize(item.title)
  const subtitle = normalize(item.subtitle || "")
  const keywords = normalize(item.keywords || "")
  const href = normalize(item.href)
  const haystack = `${title} ${subtitle} ${keywords} ${href}`

  if (title === q) return 1000
  if (title.startsWith(q)) return 900
  if (haystack.includes(q)) return 700
  if (haystack.split(/[^a-z0-9]+/).some((word) => word.startsWith(q))) return 600
  if (acronym(`${item.subtitle || ""} ${item.title}`).startsWith(q)) return 500
  if (q.length <= 3 && fuzzyMatch(q, haystack)) return 200
  return null
}

/** Filtert + scored eine statische Command-Liste synchron (kein Netzwerk). */
function scoreList(query: string, list: Command[]): Command[] {
  return list
    .flatMap((item) => {
      const score = scoreItem(query, item)
      return score === null ? [] : [{ item, score }]
    })
    .sort((a, b) => b.score - a.score || a.item.title.localeCompare(b.item.title, "de"))
    .map(({ item }) => item)
}

// Reihenfolge der Gruppen (bei Query ≥ 2): Aktionen → Seiten → Mitarbeiter → Doku → Apps
const GROUP_ORDER: CommandGroup[] = ["Aktionen", "Seiten", "Mitarbeiter", "Dokumentation", "Apps"]

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate()
  const roles = useAuthStore((s) => s.user?.roles)
  const [query, setQuery] = useState("")
  const [debouncedQuery, setDebouncedQuery] = useState("")
  const [activeIdx, setActiveIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const links = useLinks()

  const trimmedQuery = query.trim()
  const trimmedDebouncedQuery = debouncedQuery.trim()

  // Doku-Index einmalig bauen (React-frei, trivial klein).
  const docCommands = useMemo(() => buildDocCommands(), [])

  // 180 ms Debounce — betrifft NUR die Mitarbeiter-Server-Suche.
  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(query.trim()), 180)
    return () => window.clearTimeout(handle)
  }, [query])

  // Permission-Filter für statische Commands.
  function visible(list: Command[]): Command[] {
    return list.filter((c) => !c.requiredPermission || hasPermission(roles, c.requiredPermission))
  }

  // ---- Statische Gruppen: synchron, instant, 0 Netzwerk ----
  const pageItems = useMemo(
    () => scoreList(query, visible(pageCommands)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query, roles],
  )
  const actionItems = useMemo(
    () => (trimmedQuery.length >= 1 ? scoreList(query, visible(actionCommands)) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query, roles, trimmedQuery.length],
  )
  const docItems = useMemo(
    () => (trimmedQuery.length >= 2 ? scoreList(query, docCommands).slice(0, MAX_DOC_RESULTS) : []),
    [query, docCommands, trimmedQuery.length],
  )

  // ---- Apps: App-Switcher-Ziele als Gruppe (UX_KONZEPT §3.2 Punkt 4) ----
  const appCommands = useMemo(() => buildAppCommands(links, roles ?? []), [links, roles])
  const appItems = useMemo(
    () => (trimmedQuery.length >= 1 ? scoreList(query, appCommands) : []),
    [query, appCommands, trimmedQuery.length],
  )

  // ---- Mitarbeiter: debounced Server-Suche (einzige Netzwerk-Quelle) ----
  const { data: mitarbeiterPage, isFetching: employeesLoading } = useQuery({
    queryKey: ["command-palette", "employees", trimmedDebouncedQuery],
    queryFn: () => api.employees.list({ q: trimmedDebouncedQuery, size: EMPLOYEE_SEARCH_SIZE }),
    enabled: open && trimmedDebouncedQuery.length >= 2,
    staleTime: 30_000,
  })

  const employeeItems = useMemo<Command[]>(() => {
    // Anti-Flacker-Guard: alte Treffer nicht zeigen, solange der Debounce läuft.
    if (trimmedQuery.length < 2 || trimmedDebouncedQuery !== trimmedQuery) return []
    return (mitarbeiterPage?.data ?? []).map((e) => ({
      id: `emp-${e.id}`,
      title: e.displayName,
      href: `/mitarbeiter/${e.id}`,
      icon: Users,
      group: "Mitarbeiter" as const,
      subtitle: e.positionNames.join(", ") || undefined,
    }))
  }, [mitarbeiterPage, trimmedQuery, trimmedDebouncedQuery])

  // ---- Gruppen zusammenstellen ----
  const itemGroups = useMemo(() => {
    const byGroup: Record<CommandGroup, Command[]> = {
      Aktionen: actionItems,
      Seiten: pageItems,
      Mitarbeiter: employeeItems,
      Dokumentation: docItems,
      Apps: appItems,
    }
    // Leere Query: nur Seiten. Ab 1 Zeichen: alle Gruppen in fester Reihenfolge.
    const order = trimmedQuery.length === 0 ? (["Seiten"] as CommandGroup[]) : GROUP_ORDER
    return order
      .map((label) => ({ label, items: byGroup[label] }))
      .filter((g) => g.items.length > 0)
  }, [actionItems, pageItems, employeeItems, docItems, appItems, trimmedQuery.length])

  const items = useMemo(() => itemGroups.flatMap((g) => g.items), [itemGroups])

  // Reset bei Öffnen: Query leeren, Auswahl zurücksetzen, Input fokussieren.
  useEffect(() => {
    if (open) {
      setQuery("")
      setDebouncedQuery("")
      setActiveIdx(0)
      setTimeout(() => inputRef.current?.focus(), 10)
    }
  }, [open])

  // activeIdx clampen, wenn sich die Trefferzahl ändert.
  useEffect(() => {
    if (activeIdx >= items.length) setActiveIdx(Math.max(0, items.length - 1))
  }, [items.length, activeIdx])

  // Aktives Item ins Sichtfeld scrollen.
  useEffect(() => {
    if (!listRef.current) return
    const active = listRef.current.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`)
    active?.scrollIntoView({ block: "nearest" })
  }, [activeIdx])

  function selectItem(item: Command) {
    onOpenChange(false)
    if (item.onSelect) item.onSelect()
    else navigate(item.href)
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIdx((i) => Math.min(items.length - 1, i + 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIdx((i) => Math.max(0, i - 1))
    } else if (e.key === "Enter") {
      e.preventDefault()
      const item = items[activeIdx]
      if (item) selectItem(item)
    } else if (e.key === "Escape") {
      onOpenChange(false)
    }
  }

  if (!open) return null

  const loading = employeesLoading && trimmedQuery.length >= 2

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[15vh] backdrop-blur-sm"
      onClick={() => onOpenChange(false)}
      role="dialog"
      aria-modal="true"
      aria-label="Befehlspalette"
    >
      <div
        className="mx-4 w-full max-w-xl overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Suchfeld */}
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setActiveIdx(0)
            }}
            onKeyDown={handleKey}
            placeholder="Suchen oder navigieren…"
            aria-label="Suchen"
            className="flex-1 bg-transparent py-1.5 text-sm outline-none"
          />
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          <kbd className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">ESC</kbd>
        </div>

        {/* Ergebnisliste */}
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-1" role="listbox">
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              {loading ? "Suche läuft…" : "Nichts gefunden."}
            </div>
          )}
          {(() => {
            let idx = 0
            return itemGroups.map((group) => (
              <div key={group.label}>
                {itemGroups.length > 1 && (
                  <div className="px-3 pb-1 pt-2 text-[10px] font-medium uppercase text-muted-foreground">
                    {group.label}
                  </div>
                )}
                {group.items.map((item) => {
                  const currentIdx = idx++
                  const Icon = item.icon
                  const isActive = currentIdx === activeIdx
                  return (
                    <button
                      key={item.id}
                      data-idx={currentIdx}
                      role="option"
                      aria-selected={isActive}
                      onClick={() => selectItem(item)}
                      onMouseEnter={() => setActiveIdx(currentIdx)}
                      className={cn(
                        "flex w-full items-center gap-3 px-3 py-2 text-left text-sm",
                        isActive ? "bg-accent text-accent-foreground" : "hover:bg-accent/50",
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{item.title}</span>
                        {item.subtitle && (
                          <span className="block truncate text-[11px] text-muted-foreground">
                            {item.subtitle}
                          </span>
                        )}
                      </span>
                      {!item.subtitle && (
                        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                          {item.group}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            ))
          })()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t px-3 py-2 text-[10px] text-muted-foreground">
          <span>
            {items.length} Eintrag{items.length === 1 ? "" : "e"}
          </span>
          <span className="flex items-center gap-2">
            <span>↑↓ navigieren</span>
            <span>↵ öffnen</span>
          </span>
        </div>
      </div>
    </div>
  )
}
